/**
 * OT Kickoffs (PSTATS-1268): with `spots.otKickoffs` on, each overtime period
 * plays like a regulation period - kickoffs after the toss and after scores,
 * no Overtime Starting Yardline. With it off, overtime is a series of
 * alternating possessions, as it always was.
 *
 * Whether overtime is timed is a separate setting, OT Time Control
 * (`general.otTimeControl`, PSTATS-664), in either format; a timed overtime
 * period runs for OT Period Length (`general.otMinutesByPeriod`).
 *
 * Kickoff Play Entry (`general.kickoffEnabled`) is flag football's own
 * switch. On a rule set that has it, OT Kickoffs only acts while it is on,
 * so a value saved earlier and then hidden by the form never acts.
 *
 * Kept free of imports so any layer (rules, services, serializers, the
 * clock factory) can use it without a circular dependency.
 */

/**
 * Kickoff Play Entry (PSTATS-1270): opt-in, so a config saved before the key
 * existed has no kickoffs. The one definition, behind context-utils'
 * isKickoffEnabled and isOtKickoffsOn alike, so the two cannot disagree.
 * @param {object} gameConfig
 * @returns {boolean}
 */
export const isKickoffPlayEntryOn = (gameConfig) =>
    !!gameConfig?.general?.kickoffEnabled;

/**
 * @param {object} gameConfig
 * @returns {boolean} whether overtime plays like regulation
 */
export const isOtKickoffsOn = (gameConfig) => {
    const { general = {}, spots = {} } = gameConfig || {};
    const kickoffsAllowed =
        !("kickoffEnabled" in general) || isKickoffPlayEntryOn(gameConfig);

    return spots.otKickoffs === true && kickoffsAllowed;
};

/**
 * Overtime as alternating series of possessions: every overtime special case
 * that predates PSTATS-1268, other than the clock, applies only here.
 * @param {FootballGameContext} gameContext
 * @returns {boolean}
 */
export const isSeriesOvertime = (gameContext) =>
    gameContext.getOvertimePeriod() >= 1 &&
    !isOtKickoffsOn(gameContext.gameConfig);

/**
 * OT Time Control: whether overtime is timed. Off by default, and absent
 * from configs saved before a rule set had it, which read as off.
 * @param {object} gameConfig
 * @returns {boolean}
 */
export const isOvertimeTimed = (gameConfig) =>
    !!gameConfig?.general?.otTimeControl;

/**
 * Whole minutes in the given period: the OT length for an overtime period
 * while OT Time Control is on, the regulation length otherwise. A config
 * without an OT length times overtime at the regulation length, as every
 * timed overtime was before the setting existed; validateFieldConfig keeps a
 * saved length a number of at least one minute while OT Time Control is on.
 * @param {object} gameConfig
 * @param {number} period
 * @returns {number}
 */
export const getPeriodMinutes = (gameConfig, period) => {
    const { minutesByPeriod, otMinutesByPeriod, periodsInRegulation } =
        gameConfig.general;
    if (period > periodsInRegulation && isOvertimeTimed(gameConfig)) {
        return otMinutesByPeriod ?? minutesByPeriod;
    }

    return minutesByPeriod;
};

/**
 * `getPeriodMinutes` bound to one config, for arithmetic that crosses
 * periods of different lengths.
 * @param {object} gameConfig
 * @returns {function(number): number}
 */
export const periodMinutesOf = (gameConfig) => (period) =>
    getPeriodMinutes(gameConfig, period);
